/// <amd-module name="Test/TestPlansHubRefresh/TestPlanClonePanel/Redux/TestPlanClonePanelSaga" />

import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { ITestHubDataService } from "Test/TestPlansHubRefresh/Service/TestHubDataService";
import { TelemetryConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { TelemetryActions } from "Test/TestPlansHubRefresh/TestHub/Redux/TelemetryActions";
import { isAdvancedLicenseEnabled } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshSelector";
import { TestPlanClonePanelConstants } from "Test/TestPlansHubRefresh/TestPlanClonePanel/TestPlanClonePanelConstants";
import * as Resources from "Test/TestPlansHubRefresh/TestPlanClonePanel/Resources";
import { CloneTestPlanOperationInformation } from "TestPlanCommon/Contracts/TestPlan";
import { IProjectData, ITfsPageService } from "Tfs/Platform/Page";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssLocationService } from "VSS/Platform/Location";
import { TreeNodeStructureType, WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking.Types";
import { TestPlanClonePanelActions, TestPlanClonePanelActionsTypes } from "./TestPlanClonePanelActions";
import { ClassificationNodeTypeConstants, INode, INodeStructureType } from "./TestPlanClonePanelContracts";
import { SuitesActions } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesActions";
import { errorHandledSaga } from "Test/TestPlansHubRefresh/TestHub/Util";

export function* TestPlanClonePanelSaga(): SagaIterator {
    yield effects.takeLatest(TestPlanClonePanelActionsTypes.getRootNodes, _getRootNodes);
    yield effects.takeLatest(TestPlanClonePanelActionsTypes.loadModules, _loadModules);
    yield effects.takeLatest(TestPlanClonePanelActionsTypes.copyTestPlan, _copyTestPlan);
}

export function* getRootNodes(action: ActionsOfType<TestPlanClonePanelActions, TestPlanClonePanelActionsTypes.getRootNodes>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    let projectId: string = "";
    if (tfsPageData && tfsPageData.project) {
        projectId = tfsPageData.project.id;
    }

    const workItemTrackingClient: IWorkItemTrackingRestClient = (yield effects.call(
        [pageContext, pageContext.getRestClient],
        "IWorkItemTrackingRestClient"
    )) as any;
    const witNodes: WorkItemClassificationNode[] = (yield effects.call(
        [workItemTrackingClient, workItemTrackingClient.getRootNodes],
        projectId,
        TestPlanClonePanelConstants.AreaPathIterationDepth
    )) as any;

    let nodes: INode[] = getFields(witNodes, tfsPageData.project);
    yield effects.put(TestPlanClonePanelActions.getRootNodesSucceeded(nodes[0], nodes[1]));
}

export function* loadModules(action: ActionsOfType<TestPlanClonePanelActions, TestPlanClonePanelActionsTypes.getRootNodes>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const legacyPlatformService = (yield effects.call([pageContext, pageContext.getService], "ILegacyPlatformService")) as any;
    yield effects.call([legacyPlatformService, legacyPlatformService.loadModules], ["TestManagement/Scripts/Shared/AreaPathAndIterationPicker"]);
    yield effects.put(TestPlanClonePanelActions.loadModulesSucceeded());
}

export function* copyTestPlan(action: ActionsOfType<TestPlanClonePanelActions, TestPlanClonePanelActionsTypes.copyTestPlan>): SagaIterator {
    try {
        yield effects.put(TestPlanClonePanelActions.copyTestPlanInProgress());
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

        const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
        let cloneTestPlanOperationInformation: CloneTestPlanOperationInformation = (yield effects.call(
            [dataService, dataService.cloneTestPlan],
            action.payload.oldPlanId,
            action.payload.newPlanName,
            action.payload.areatPath,
            action.payload.iteration,
            action.payload.isDeepClone
        )) as any;
        let planId: number = cloneTestPlanOperationInformation.destinationTestPlan ? cloneTestPlanOperationInformation.destinationTestPlan.id : -1;
        let opId: number = cloneTestPlanOperationInformation.cloneOperationResponse
            ? cloneTestPlanOperationInformation.cloneOperationResponse.opId
            : -1;
        if (planId > 0 && opId > 0) {
            const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
            const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
            let projectName: string = "";
            if (tfsPageData && tfsPageData.project) {
                projectName = tfsPageData.project.name;
            }

            const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;
            const routeValues: { [key: string]: string } = {
                project: projectName
            };
            //constructing new plan url using contribution id
            let url = (yield effects.call(
                [locationService, locationService.routeUrl],
                TestPlanClonePanelConstants.contributionId,
                routeValues
            )) as any;
            url += format(TestPlanClonePanelConstants.queryParams, planId, opId);

            let newWindow = (yield effects.call([window, window.open], url, "_blank")) as any;
            if (newWindow) {
                newWindow.opener = null;
            }

            const telemetryPayload = {
                newPlanId: planId,
                iteration: action.payload.iteration,
                areaPath: action.payload.areatPath,
                planName: action.payload.newPlanName,
                opId: opId,
                sourcePlanId: action.payload.oldPlanId,
                isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled),
                idDeepClone: action.payload.isDeepClone
            };
            yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.COPY_TEST_PLAN, telemetryPayload));

            yield effects.put(TestPlanClonePanelActions.copyTestPlanSucceded(planId, opId));
        }
    } catch (e) {
        yield effects.put(SuitesActions.showToastNotifications(Resources.CloneOperationFailed + " " + e.message, TestPlanClonePanelConstants.ToastDurationMS));
        yield effects.put(TestPlanClonePanelActions.copyTestPlanFailed());
        console.log(e);
    }
}

// To DO:  Remove this below function once we consume the picker via WIT new platform. These codes
//         should not be a part of our area

// Get the area path and iteration fileds from the WorkItemClassificationNode. It does a transation from WorkItemClassificationNode[] tp INode[]
function getFields(nodes: WorkItemClassificationNode[], project: IProjectData): INode[] {
    const topLevelProjectNode: INode = {
        id: parseInt(project.id),
        name: project.name,
        guid: undefined,
        structure: 0,
        type: ClassificationNodeTypeConstants.ProjectType,
        children: []
    };

    for (const node of nodes) {
        topLevelProjectNode.children.push(mapClassificationNodesToLegacyNode(node));
    }

    let fields: INode[] = [];
    fields.push(findChildByStructure(topLevelProjectNode, 1));
    fields.push(findChildByStructure(topLevelProjectNode, 2));
    return fields;
}

// Get the areaPath/iteration child node base on the structure
function findChildByStructure(node: INode, structure: number): INode {
    const children = node.children;
    let childNode: INode = {} as INode;
    if (children) {
        for (const child of children) {
            if (child.structure === structure) {
                return child;
            }
        }
    }
    return childNode;
}

function mapClassificationNodesToLegacyNode(node: WorkItemClassificationNode): INode {
    const legacyNode: INode = {
        id: node.id,
        structure: undefined,
        startDate: node.attributes ? new Date(node.attributes["startDate"]) : undefined,
        children: [],
        finishDate: node.attributes ? new Date(node.attributes["finishDate"]) : undefined,
        guid: node.identifier,
        name: node.name,
        parent: undefined,
        type: undefined
    };

    if (node.structureType === TreeNodeStructureType.Area) {
        legacyNode.structure = INodeStructureType.Area;
        legacyNode.type = ClassificationNodeTypeConstants.AreaType;
    } else if (node.structureType === TreeNodeStructureType.Iteration) {
        legacyNode.structure = INodeStructureType.Iteration;
        legacyNode.type = ClassificationNodeTypeConstants.IterationType;
    }

    if (node.children) {
        for (const childNode of node.children) {
            legacyNode.children.push(mapClassificationNodesToLegacyNode(childNode));
        }
    }
    return legacyNode;
}

const _getRootNodes = errorHandledSaga(function* (
    action: ActionsOfType<TestPlanClonePanelActions, TestPlanClonePanelActionsTypes.getRootNodes>
): SagaIterator {
    yield effects.call(getRootNodes, action);
});

const _loadModules = errorHandledSaga(function* (
    action: ActionsOfType<TestPlanClonePanelActions, TestPlanClonePanelActionsTypes.getRootNodes>
): SagaIterator {
    yield effects.call(loadModules, action);
});

const _copyTestPlan = errorHandledSaga(function* (
    action: ActionsOfType<TestPlanClonePanelActions, TestPlanClonePanelActionsTypes.copyTestPlan>
): SagaIterator {
    yield effects.call(copyTestPlan, action);
});